import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { ENV } from '../../../config/env';
import toast from 'react-hot-toast';

interface GeneratedDescription {
  short: string;
  long: string;
  bullets: string[];
  seoKeywords: string[];
}

const ProductDescriptionTab: React.FC = () => {
  const { user, getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Form inputs
  const [productName, setProductName] = useState('');
  const [features, setFeatures] = useState('');
  const [targetAudience, setTargetAudience] = useState('General consumers');
  const [tone, setTone] = useState('Professional');
  const [keywords, setKeywords] = useState('');
  
  // Results
  const [generatedDescription, setGeneratedDescription] = useState<GeneratedDescription | null>(null);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!productName || !features) {
      toast.error('Please provide product name and features');
      return;
    }

    try {
      setLoading(true);
      toast.loading('Generating product descriptions...');

      const userId = await getToken();
      
      if (!userId) {
        toast.error('Authentication error. Please log in again.');
        return;
      }

      const response = await fetch(
        `${ENV.API_URL}/api/marketing/product-description`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId,
            productName,
            features,
            targetAudience,
            tone,
            keywords: keywords.trim() ? keywords.split(',').map(k => k.trim()).filter(k => k) : []
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate descriptions');
      }

      const data = await response.json();
      toast.dismiss();
      setGeneratedDescription(data.descriptions);
      toast.success('Product descriptions generated successfully!');
    } catch (error) {
      toast.dismiss();
      console.error('Error generating descriptions:', error);
      toast.error(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${type} copied to clipboard!`);
  };

  const newDescription = () => {
    setGeneratedDescription(null);
    setProductName('');
    setFeatures('');
    setKeywords('');
  };

  if (!generatedDescription) {
    return (
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left column - Input form */}
        <div className="w-full lg:w-2/5 bg-gradient-to-br from-orange-50/50 to-red-50/50 rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 mb-5">Product Details</h3>
          
          <form onSubmit={handleGenerate} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Product Name *
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 text-gray-800"
                placeholder="e.g., Wireless Bluetooth Headphones"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Key Features & Benefits *
              </label>
              <textarea
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 text-gray-800 h-32"
                placeholder="e.g., Active noise cancellation, 30-hour battery life, comfortable over-ear design, premium sound quality..."
                value={features}
                onChange={(e) => setFeatures(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Target Audience
              </label>
              <select
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 text-gray-800"
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
              >
                <option value="General consumers">General consumers</option>
                <option value="Tech enthusiasts">Tech enthusiasts</option>
                <option value="Professionals">Professionals</option>
                <option value="Students">Students</option>
                <option value="Fitness enthusiasts">Fitness enthusiasts</option>
                <option value="Gamers">Gamers</option>
                <option value="Luxury shoppers">Luxury shoppers</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tone
              </label>
              <select
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 text-gray-800"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
              >
                <option value="Professional">Professional</option>
                <option value="Casual">Casual & Friendly</option>
                <option value="Luxury">Luxury & Premium</option>
                <option value="Enthusiastic">Enthusiastic & Energetic</option>
                <option value="Technical">Technical & Detailed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                SEO Keywords (Optional)
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 text-gray-800"
                placeholder="e.g., wireless headphones, noise cancelling, bluetooth"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">Comma-separated keywords</p>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                className="w-full py-3 px-6 border border-transparent rounded-xl shadow-lg text-white font-medium text-base bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all duration-200"
                disabled={loading}
              >
                {loading ? 'Generating...' : 'Generate Descriptions'}
              </button>
            </div>
          </form>
        </div>

        {/* Right column - Preview/Instructions */}
        <div className="w-full lg:w-3/5 bg-white rounded-xl p-6 border border-gray-200">
          <div className="text-center py-12">
            <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No descriptions generated yet</h3>
            <p className="text-gray-500 mb-6">Fill out the form and click "Generate Descriptions"</p>
            
            <div className="mt-8 text-left max-w-md mx-auto">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">✨ What You'll Get:</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="text-orange-600 mr-2">•</span>
                  <span><strong>Short Description</strong> - Perfect for product cards and listings</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-600 mr-2">•</span>
                  <span><strong>Long Description</strong> - Detailed copy for product pages</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-600 mr-2">•</span>
                  <span><strong>Bullet Points</strong> - Scannable key features and benefits</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-600 mr-2">•</span>
                  <span><strong>SEO Keywords</strong> - Optimized keywords for search visibility</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left column - Metadata */}
      <div className="w-full lg:w-2/5 space-y-4">
        <div className="bg-gradient-to-br from-orange-50/50 to-red-50/50 rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Product Info</h3>
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="font-semibold text-gray-700">Product:</dt>
              <dd className="text-gray-600 mt-1">{productName}</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-700">Target Audience:</dt>
              <dd className="text-gray-600 mt-1">{targetAudience}</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-700">Tone:</dt>
              <dd className="text-gray-600 mt-1">{tone}</dd>
            </div>
            {keywords && (
              <div>
                <dt className="font-semibold text-gray-700">Keywords:</dt>
                <dd className="text-gray-600 mt-1">{keywords}</dd>
              </div>
            )}
          </dl>
        </div>

        <div className="space-y-2">
          <button
            onClick={newDescription}
            className="w-full py-2 px-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
          >
            Generate New Descriptions
          </button>
        </div>
      </div>

      {/* Right column - Generated Content */}
      <div className="w-full lg:w-3/5 bg-white rounded-xl p-6 border border-gray-200 space-y-6">
        {/* Short Description */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-lg font-bold text-gray-900">Short Description</h4>
            <button
              onClick={() => copyToClipboard(generatedDescription.short, 'Short description')}
              className="text-sm text-orange-600 hover:text-orange-700 font-medium"
            >
              Copy
            </button>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-gray-700">{generatedDescription.short}</p>
          </div>
        </div>

        {/* Long Description */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-lg font-bold text-gray-900">Long Description</h4>
            <button
              onClick={() => copyToClipboard(generatedDescription.long, 'Long description')}
              className="text-sm text-orange-600 hover:text-orange-700 font-medium"
            >
              Copy
            </button>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-gray-700 whitespace-pre-wrap">{generatedDescription.long}</p>
          </div>
        </div>

        {/* Bullet Points */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-lg font-bold text-gray-900">Key Features (Bullets)</h4>
            <button
              onClick={() => copyToClipboard(generatedDescription.bullets.map(b => `• ${b}`).join('\n'), 'Bullet points')}
              className="text-sm text-orange-600 hover:text-orange-700 font-medium"
            >
              Copy
            </button>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <ul className="space-y-2">
              {generatedDescription.bullets.map((bullet, index) => (
                <li key={index} className="flex items-start text-gray-700">
                  <span className="text-orange-600 mr-2">•</span>
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* SEO Keywords */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-lg font-bold text-gray-900">SEO Keywords</h4>
            <button
              onClick={() => copyToClipboard(generatedDescription.seoKeywords.join(', '), 'SEO keywords')}
              className="text-sm text-orange-600 hover:text-orange-700 font-medium"
            >
              Copy
            </button>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex flex-wrap gap-2">
              {generatedDescription.seoKeywords.map((keyword, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDescriptionTab;
